import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { getUserInvitationByToken, acceptInvitation } from '../firebase/invitations';
import logoSvg from '../assets/logo.svg';
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';

const InvitationAcceptPage = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const emailFromUrl = searchParams.get('email');
  const navigate = useNavigate();
  
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [step, setStep] = useState('loading'); // loading, form, success, error
  
  const [formData, setFormData] = useState({
    displayName: '',
    password: '',
    confirmPassword: '',
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchInvitation = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const invitationData = await getUserInvitationByToken(token);
        
        if (!invitationData) {
          setError('Este convite não existe ou expirou');
          setStep('error');
          return;
        }
        
        if (invitationData.status === 'accepted') {
          setError('Este convite já foi aceito. Faça login normalmente.');
          setStep('error');
          return;
        }
        
        if (invitationData.status === 'expired') {
          setError('Este convite expirou. Solicite um novo convite ao administrador do salão.');
          setStep('error');
          return;
        }
        
        // Verificar se o email da URL corresponde ao email do convite
        if (emailFromUrl && emailFromUrl.toLowerCase() !== invitationData.email.toLowerCase()) {
          setError('O email da URL não corresponde ao email do convite.');
          setStep('error');
          return;
        }
        
        setInvitation(invitationData);
        setStep('form');
      } catch (error) {
        console.error('Erro ao buscar convite:', error);
        setError('Ocorreu um erro ao buscar o convite. Tente novamente mais tarde.');
        setStep('error');
      } finally {
        setLoading(false);
      }
    };
    
    fetchInvitation();
  }, [token, emailFromUrl]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Limpar erro do campo quando usuário digita
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: null
      });
    }
  };
  
  const validateForm = () => {
    const errors = {};
    
    if (!formData.displayName.trim()) {
      errors.displayName = 'Nome é obrigatório';
    }
    
    if (!formData.password) {
      errors.password = 'Senha é obrigatória';
    } else if (formData.password.length < 6) {
      errors.password = 'A senha deve ter pelo menos 6 caracteres';
    }
    
    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'As senhas não coincidem';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setSubmitting(true);
      setError(null);
      
      const result = await acceptInvitation(
        token, 
        formData.displayName,
        formData.password
      );
      
      // Fazer login automático após aceitar o convite
      const auth = getAuth();
      await signInWithEmailAndPassword(auth, invitation.email, formData.password);
      
      setStep('success');
      
      // Aguardar um momento antes de redirecionar
      setTimeout(() => {
        if (result.role === 'professional') {
          navigate('/professional');
        } else {
          navigate(`/${result.salon.id}/admin`);
        }
      }, 3000);
    } catch (error) {
      console.error('Erro ao aceitar convite:', error);
      setError(error.message || 'Ocorreu um erro ao aceitar o convite. Tente novamente mais tarde.');
    } finally {
      setSubmitting(false);
    }
  };
  
  const renderStep = () => {
    switch (step) {
      case 'loading':
        return (
          <div className="flex flex-col items-center justify-center p-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
            <p className="text-gray-600">Verificando convite...</p>
          </div>
        );
        
      case 'form':
        return (
          <div className="p-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Aceitar Convite</h2>
            <p className="text-gray-600 mb-6">
              Você foi convidado por {invitation?.salonName || 'um salão'} para se juntar como {' '}
              {invitation?.role === 'professional' ? 'profissional' : 'gestor'}.
            </p>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <div className="mt-1">
                  <input
                    type="email"
                    id="email"
                    value={invitation?.email || ''}
                    readOnly
                    className="bg-gray-100 shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md cursor-not-allowed"
                  />
                  <p className="mt-1 text-xs text-gray-500">Este é o email associado ao seu convite</p>
                </div>
              </div>
              
              <div>
                <label htmlFor="displayName" className="block text-sm font-medium text-gray-700">
                  Nome Completo
                </label>
                <div className="mt-1">
                  <input
                    type="text"
                    id="displayName"
                    name="displayName"
                    value={formData.displayName}
                    onChange={handleChange}
                    className={`shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md ${
                      formErrors.displayName ? 'border-red-300' : ''
                    }`}
                    required
                  />
                  {formErrors.displayName && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.displayName}</p>
                  )}
                </div>
              </div>
              
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Senha
                </label>
                <div className="mt-1">
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md ${
                      formErrors.password ? 'border-red-300' : ''
                    }`}
                    required
                  />
                  {formErrors.password && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.password}</p>
                  )}
                </div>
              </div>
              
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  Confirmar Senha
                </label>
                <div className="mt-1">
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md ${
                      formErrors.confirmPassword ? 'border-red-300' : ''
                    }`}
                    required
                  />
                  {formErrors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.confirmPassword}</p>
                  )}
                </div>
              </div>
              
              {error && (
                <div className="rounded-md bg-red-50 p-4">
                  <div className="flex">
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">Erro</h3>
                      <div className="mt-2 text-sm text-red-700">
                        <p>{error}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <span className="mr-2">Criando conta</span>
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </>
                  ) : (
                    'Aceitar Convite'
                  )}
                </button>
              </div>
            </form>
          </div>
        );
        
      case 'success':
        return (
          <div className="p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 text-green-600 mb-6">
              <svg className="h-10 w-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Convite Aceito!</h2>
            <p className="text-gray-600 mb-6">
              Sua conta foi criada com sucesso e você já está conectado. Você será redirecionado automaticamente.
            </p>
            
            <div className="animate-pulse">Redirecionando...</div>
          </div>
        );
        
      case 'error':
        return (
          <div className="p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 text-red-600 mb-6">
              <svg className="h-10 w-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </div>
            
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">Convite Inválido</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            
            <button
              onClick={() => navigate('/')}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Voltar para a Página Inicial
            </button>
          </div>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm py-4">
        <div className="container mx-auto px-4">
          <div className="flex justify-center items-center">
            <img src={logoSvg} alt="Bookly" className="h-14" />
          </div>
        </div>
      </header>
      
      {/* Main content */}
      <main className="flex-grow container mx-auto px-4 py-8">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
          {renderStep()}
        </div>
      </main>
      
      {/* Footer */}
      <footer className="bg-white py-4 border-t border-gray-200">
        <div className="container mx-auto px-4 text-center text-sm text-gray-500">
          © {new Date().getFullYear()} Bookly · Todos os direitos reservados
        </div>
      </footer>
    </div>
  );
};

export default InvitationAcceptPage; 